// src/store/slices/authSlice.ts
import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import {STORAGE_KEYS} from "../../utils/constants";
import type {AuthState, AuthUser} from "../../types";

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading true to prevent premature route checks
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{user: AuthUser; token: string}>
    ) => {
      const {user, token} = action.payload;
      state.user = user;
      state.token = token;
      state.isAuthenticated = true;
      state.isLoading = false;

      // Persist to localStorage
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    },

    setUser: (state, action: PayloadAction<AuthUser>) => {
      state.user = action.payload;
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(action.payload));
    },

    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;

      // Clear localStorage
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    initializeAuth: (state) => {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const userStr = localStorage.getItem(STORAGE_KEYS.USER);

      if (token && userStr) {
        try {
          const user = JSON.parse(userStr) as AuthUser;
          const tokenParts = token.split(".");

          if (tokenParts.length === 3 && tokenParts[1]) {
            try {
              const payload = JSON.parse(atob(tokenParts[1]));
              const currentTime = Math.floor(Date.now() / 1000);

              const bufferTime = 1 * 60; // 1 minute buffer (reduced from 5 minutes)
              const timeUntilExpiry = payload.exp - currentTime;

              // Token validation logic

              if (payload.exp && timeUntilExpiry > bufferTime) {
                state.token = token;
                state.user = user;
                state.isAuthenticated = true;
                state.isLoading = false;
                // Token is valid, user logged in
                return; // Exit early if token is valid
              } else {
                // Token expired or expiring soon
              }
            } catch (decodeError) {
              // Invalid token format
            }
          }
        } catch (error) {
          // Error parsing user data
        }
      }

      // If token is invalid, expired, or not present, reset to logout state
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
    },
  },
});

export const {setCredentials, setUser, logout, setLoading, initializeAuth} =
  authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectCurrentUser = (state: {auth: AuthState}) => state.auth.user;
export const selectCurrentToken = (state: {auth: AuthState}) =>
  state.auth.token;
export const selectIsAuthenticated = (state: {auth: AuthState}) =>
  state.auth.isAuthenticated;
export const selectAuthLoading = (state: {auth: AuthState}) =>
  state.auth.isLoading;
